import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { X, Camera, MapPin, Heart, Users, Bell, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';

const API = '/api'; // Use relative URL for same-origin API calls

const CHECKLIST_ICONS = {
  profile_picture: Camera,
  about_me: ChevronRight,
  nationality: MapPin,
  residence_city: MapPin,
  interests: Heart,
  groups_joined: Users,
  notifications_enabled: Bell,
};

export default function ProfileCompletionPopup() {
  const { user } = useAuth();
  const isIOSApp = useIsIOSApp();
  const [show, setShow] = useState(false);
  const [completionData, setCompletionData] = useState(null);

  useEffect(() => {
    if (!user) return;
    const fetchCompletion = async () => {
      try {
        const { data } = await axios.get(`${API}/users/me/profile-completion`);
        if (data.show_reminder && !data.is_complete) {
          setCompletionData(data);
          setShow(true);
        }
      } catch {
        // Silently fail
      }
    };
    // Delay to avoid overwhelming user on page load
    const timer = setTimeout(fetchCompletion, 3000);
    return () => clearTimeout(timer);
  }, [user]);

  const handleDismiss = async () => {
    setShow(false);
    try {
      await axios.post(`${API}/users/me/dismiss-profile-reminder`);
    } catch {
      // Silently fail
    }
  };

  const handleGoToProfile = () => {
    setShow(false);
    window.location.href = '/profile';
  };

  if (!show || !completionData) return null;

  const { percentage, missing_items } = completionData;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          data-testid="profile-completion-popup"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 to-teal-500 p-6 text-white relative">
              <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
                data-testid="dismiss-popup-btn"
              >
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold mb-1">Complete Your Profile</h2>
              <p className="text-white/80 text-sm">
                Stand out and connect with fellow expats
              </p>
              {/* Progress bar */}
              <div className="mt-4 bg-white/20 rounded-full h-3 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  className="h-full bg-white rounded-full"
                />
              </div>
              <p className="text-sm text-white/90 mt-1 font-medium">{percentage}% complete</p>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-1">
                {isIOSApp
                  ? 'Complete your profile to get the most out of ExpatsSphere!'
                  : 'Your ExpatsSphere membership is complimentary for 2 years from signup.'}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Members with complete profiles get <strong>3x more connections</strong>.
              </p>

              {/* Checklist */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {missing_items.map((item) => {
                  const Icon = CHECKLIST_ICONS[item.key] || ChevronRight;
                  return (
                    <div
                      key={item.key}
                      className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => { window.location.href = item.link; }}
                      data-testid={`checklist-item-${item.key}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-violet-600" />
                      </div>
                      <span className="text-sm text-gray-700 flex-1">{item.label}</span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="mt-5 flex gap-3">
                <button
                  onClick={handleGoToProfile}
                  className="flex-1 bg-violet-600 hover:bg-violet-700 text-white py-2.5 px-4 rounded-xl font-medium text-sm transition-colors"
                  data-testid="complete-profile-btn"
                >
                  Complete Profile
                </button>
                <button
                  onClick={handleDismiss}
                  className="text-gray-500 hover:text-gray-700 py-2.5 px-4 rounded-xl text-sm transition-colors"
                  data-testid="remind-later-btn"
                >
                  Later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
